import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { isLineVisibleForRole, useFirmType } from '@folio/hooks';
import {
  getDefaultLines,
  getTotals,
  getQuoteLineTranslationKeys,
  resolvePresetAmount,
} from '@folio/utils/price-display-utils';
import { checkForDifferentValues } from '@folio/utils/checkForDifferentValues';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useSuspenseQueries } from '@tanstack/react-query';
import {
  pricingControllerGetPriceQueryOptions,
  type CellType,
  type GetPriceOutput,
  type PresetKey,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

type PremiumCompareModuleProps = {
  branches: Array<string | undefined>;
};

type CompareTableRowProps = {
  label: string;
  valuesByBranchId: Record<string, number | undefined>;
  labelClassName?: string;
  type?: CellType;
};

function CompareTableRow({
  label,
  valuesByBranchId,
  labelClassName,
  type,
}: Readonly<CompareTableRowProps>) {
  const values = Object.values(valuesByBranchId);
  const hasDifferentValues = checkForDifferentValues(values);

  return (
    <TableRow className={hasDifferentValues ? 'bg-slate-100' : ''}>
      <TableCell className={labelClassName}>{label}</TableCell>
      {Object.entries(valuesByBranchId).map(([branchId, value]) => (
        <DataCell key={branchId} type={type || 'price'} className="p-2 w-2/5" value={value} />
      ))}
    </TableRow>
  );
}

export function PremiumCompareModule({ branches }: Readonly<PremiumCompareModuleProps>) {
  const { t } = useTranslation();
  const { firmType } = useFirmType();
  const { policy } = usePolicyOnDashboard();

  const { productsConfig } = useKorintProductConfig();
  const productConfig = productsConfig[policy.productId];
  const priceBreakdownConfig = productConfig?.priceBreakdownConfig;

  const priceBreakdownResults = useSuspenseQueries({
    queries: branches.map(branchId => ({
      ...pricingControllerGetPriceQueryOptions(policy.id, { branchId }),
    })),
  });

  const priceByBranchId: Record<string, GetPriceOutput> = {};
  for (let i = 0; i < branches.length; i++) {
    const branchKey = branches[i] ?? 'BASE';
    if (priceBreakdownResults[i]?.data) {
      priceByBranchId[branchKey] = priceBreakdownResults[i].data;
    }
  }

  // Merge line keys across all branches (preserving order from first branch that has each key)
  const allLineKeys: string[] = [];
  const lineByBranchAndKey: Record<
    string,
    Record<
      string,
      {
        amount: number;
        adjustedAmount?: number;
        type?: string;
        subLines?: Array<{
          key: string;
          amount: number;
          adjustedAmount?: number;
          forTypes: string[];
          type?: string;
        }>;
      }
    >
  > = {};

  for (const [branchKey, price] of Object.entries(priceByBranchId)) {
    lineByBranchAndKey[branchKey] = {};
    const hasDisplayLines = (price.lines?.length ?? 0) > 0 || !!price.totalLine;
    const lines = hasDisplayLines ? (price.lines ?? []) : getDefaultLines(price);
    for (const line of lines) {
      if (!isLineVisibleForRole(line, firmType)) continue;
      if (!allLineKeys.includes(line.key)) allLineKeys.push(line.key);
      lineByBranchAndKey[branchKey][line.key] = line;
    }
  }

  // Collect all sub-line keys per parent line
  const subLineKeysByParent: Record<string, string[]> = {};
  for (const lineKey of allLineKeys) {
    const subKeys: string[] = [];
    for (const branchKey of Object.keys(priceByBranchId)) {
      const line = lineByBranchAndKey[branchKey]?.[lineKey];
      for (const sub of line?.subLines ?? []) {
        if (!isLineVisibleForRole(sub, firmType)) continue;
        if (!subKeys.includes(sub.key)) subKeys.push(sub.key);
      }
    }
    subLineKeysByParent[lineKey] = subKeys;
  }

  const configuredLines = priceBreakdownConfig?.groups?.flatMap(g => g.lines);
  const filteredLineKeys: string[] = configuredLines
    ? configuredLines
        .map(l => l.key)
        .filter(key => {
          const line = configuredLines.find(l => l.key === key);
          return line?.type === 'PRESET' || allLineKeys.includes(key);
        })
    : allLineKeys;

  const presetKeys = new Set<string>(
    configuredLines?.filter(l => l.type === 'PRESET').map(l => l.key) ?? [],
  );

  const hasConfiguredTotals = !!priceBreakdownConfig?.totals;

  const frequency = Object.values(priceByBranchId)[0]?.frequency;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t([`PremiumCard.title_${frequency}`, 'PremiumCard.title'])}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table className="[&_tr]:border-0 table-fixed">
          <TableHeader className="[&_tr]:border-0">
            <TableRow>
              <TableHead />
              {branches.map((branch, index) => (
                <TableHead key={branch || index}>
                  {index === 0 && branches.length > 1
                    ? t('compareTable.previousPrice')
                    : t('compareTable.newPrice')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLineKeys.map(lineKey => {
              if (presetKeys.has(lineKey)) {
                const valuesByBranchId: Record<string, number | undefined> = {};
                for (const [branchKey, price] of Object.entries(priceByBranchId)) {
                  valuesByBranchId[branchKey] = resolvePresetAmount(price, lineKey as PresetKey);
                }
                return (
                  <CompareTableRow
                    key={lineKey}
                    label={t(getQuoteLineTranslationKeys(policy.productId, lineKey))}
                    valuesByBranchId={valuesByBranchId}
                    labelClassName="pr-12 font-semibold text-sm"
                  />
                );
              }

              const refLine = Object.values(lineByBranchAndKey).find(m => m[lineKey])?.[lineKey];

              const valuesByBranchId: Record<string, number | undefined> = {};
              for (const [branchKey] of Object.entries(priceByBranchId)) {
                const line = lineByBranchAndKey[branchKey]?.[lineKey];
                valuesByBranchId[branchKey] = line
                  ? (line.adjustedAmount ?? line.amount)
                  : undefined;
              }

              return (
                <React.Fragment key={lineKey}>
                  <CompareTableRow
                    label={t(getQuoteLineTranslationKeys(policy.productId, lineKey))}
                    valuesByBranchId={valuesByBranchId}
                    labelClassName="pr-12 font-semibold text-sm"
                    type={(refLine?.type as CellType) || 'price'}
                  />
                  {subLineKeysByParent[lineKey]?.map(subKey => {
                    const refSub = Object.values(lineByBranchAndKey)
                      .map(m => m[lineKey]?.subLines?.find(s => s.key === subKey))
                      .find(Boolean);

                    const subValuesByBranchId: Record<string, number | undefined> = {};
                    for (const branchKey of Object.keys(priceByBranchId)) {
                      const sub = lineByBranchAndKey[branchKey]?.[lineKey]?.subLines?.find(
                        s => s.key === subKey,
                      );
                      subValuesByBranchId[branchKey] = sub
                        ? (sub.adjustedAmount ?? sub.amount)
                        : undefined;
                    }

                    return (
                      <CompareTableRow
                        key={subKey}
                        label={t(getQuoteLineTranslationKeys(policy.productId, subKey))}
                        valuesByBranchId={subValuesByBranchId}
                        labelClassName="pr-12 text-sm"
                        type={(refSub?.type as CellType) || 'price'}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}

            {hasConfiguredTotals && priceBreakdownConfig
              ? priceBreakdownConfig.totals?.map(totalConfig => {
                  const key = totalConfig.key;
                  const valuesByBranchId: Record<string, number | undefined> = {};
                  for (const [branchKey, price] of Object.entries(priceByBranchId)) {
                    valuesByBranchId[branchKey] = resolvePresetAmount(price, key);
                  }
                  const label = t(getQuoteLineTranslationKeys(policy.productId, key));
                  return (
                    <CompareTableRow
                      key={key}
                      label={label}
                      valuesByBranchId={valuesByBranchId}
                      labelClassName="pr-12 text-secondary font-semibold text-sm"
                    />
                  );
                })
              : getTotals(Object.values(priceByBranchId)[0]).map(total => {
                  const valuesByBranchId: Record<string, number | undefined> = {};
                  for (const [branchKey, branchPrice] of Object.entries(priceByBranchId)) {
                    valuesByBranchId[branchKey] = getTotals(branchPrice).find(
                      t => t.key === total.key,
                    )?.amount;
                  }
                  return (
                    <CompareTableRow
                      key={total.key}
                      label={t(getQuoteLineTranslationKeys(policy.productId, total.key))}
                      valuesByBranchId={valuesByBranchId}
                      labelClassName="pr-12 text-secondary font-semibold text-sm"
                    />
                  );
                })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
